export type ProgressVisualState = 'completed' | 'active' | 'pending';

export type ProgressIssueKind = 'completed_with_issue' | 'not_completed';

export interface AiTaskProgressPresentationInput {
	status: string;
	currentStage: string | null;
	totalChapters: number;
	article: string;
	podcastScript: string;
	audio: { completed: number; total: number; failed: number };
	video: { completed: number; total: number; failed: number };
	failedStage: string | null;
	errorCode: string | null;
}

export interface ProgressPresentationStage {
	key: 'prepare' | 'article' | 'script' | 'audio' | 'video';
	label: 'Prepare' | 'Article' | 'Podcast Script' | 'Audio' | 'Video';
	state: ProgressVisualState;
	countLabel: string | null;
}

export interface ProgressIssue {
	kind: ProgressIssueKind;
	text: string;
}

export interface AiTaskProgressPresentation {
	stages: ProgressPresentationStage[];
	summaryLabel: 'Queued' | 'Processing' | 'Completed' | 'Not completed';
	summaryRunning: boolean;
	issue: ProgressIssue | null;
}

type TerminalStatus = 'completed' | 'completed_with_errors' | 'failed' | 'cancelled' | null;

function terminalStatus(progressStatus: string, jobStatus?: string): TerminalStatus {
	if (jobStatus && ['completed', 'completed_with_errors', 'failed', 'cancelled'].includes(jobStatus)) return jobStatus as TerminalStatus;
	if (['completed', 'completed_with_errors', 'failed', 'cancelled'].includes(progressStatus)) return progressStatus as TerminalStatus;
	return null;
}

function rawStageState(status: string, currentStage: string | null, stage: 'article' | 'script'): ProgressVisualState {
	if (status === 'completed') return 'completed';
	const canonicalStage = stage === 'script' ? 'podcast_script' : stage;
	if (status === 'generating' || currentStage === canonicalStage) return 'active';
	return 'pending';
}

function prepareState(progress: AiTaskProgressPresentationInput): ProgressVisualState {
	if (progress.currentStage === 'prepare') return 'active';
	if (
		progress.article !== 'pending'
		|| progress.podcastScript !== 'pending'
		|| ['article', 'podcast_script', 'article_ready', 'audio', 'video'].includes(progress.currentStage ?? '')
	) return 'completed';
	return 'pending';
}

function count(value: number): number {
	return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0;
}

function mediaTotal(mediaTotal: number, totalChapters: number): number {
	const normalizedMediaTotal = count(mediaTotal);
	return normalizedMediaTotal > 0 ? normalizedMediaTotal : count(totalChapters);
}

function mediaCountLabel(completed: number, total: number): string | null {
	return total > 0 ? `${count(completed)}/${total}` : null;
}

export function buildAiTaskProgressPresentation(
	progress: AiTaskProgressPresentationInput,
	jobStatus?: string,
	topicAvailable = true,
): AiTaskProgressPresentation {
	const articleReady = progress.article === 'completed' && progress.podcastScript === 'completed';
	const terminal = terminalStatus(progress.status, jobStatus);
	const audioTotal = articleReady ? mediaTotal(progress.audio.total, progress.totalChapters) : 0;
	const videoTotal = articleReady ? mediaTotal(progress.video.total, progress.totalChapters) : 0;
	const audioCompleted = audioTotal > 0
		&& count(progress.audio.completed) >= audioTotal
		&& count(progress.audio.failed) === 0;
	const videoCompleted = videoTotal > 0
		&& count(progress.video.completed) >= videoTotal
		&& count(progress.video.failed) === 0;
	const videoProgressExists = count(progress.video.completed) > 0
		|| count(progress.video.total) > 0
		|| count(progress.video.failed) > 0
		|| progress.currentStage === 'video';

	const stages: ProgressPresentationStage[] = [
		{ key: 'prepare', label: 'Prepare', state: prepareState(progress), countLabel: null },
		{ key: 'article', label: 'Article', state: rawStageState(progress.article, progress.currentStage, 'article'), countLabel: null },
		{ key: 'script', label: 'Podcast Script', state: rawStageState(progress.podcastScript, progress.currentStage, 'script'), countLabel: null },
		{
			key: 'audio',
			label: 'Audio',
			state: audioCompleted ? 'completed' : articleReady ? 'active' : 'pending',
			countLabel: articleReady ? mediaCountLabel(progress.audio.completed, audioTotal) : null,
		},
		{
			key: 'video',
			label: 'Video',
			state: videoCompleted ? 'completed' : articleReady && videoProgressExists ? 'active' : 'pending',
			countLabel: articleReady ? mediaCountLabel(progress.video.completed, videoTotal) : null,
		},
	];

	if (terminal) {
		for (const stage of stages) {
			if (stage.state !== 'completed') stage.state = 'pending';
		}
	}

	if (terminal) {
		if (!topicAvailable) {
			return {
				stages,
				summaryLabel: 'Not completed',
				summaryRunning: false,
				issue: {
					kind: 'not_completed',
					text: 'We couldn’t complete this topic. You can try creating it again.',
				},
			};
		}
		if (articleReady && audioCompleted && videoCompleted) {
			return {
				stages,
				summaryLabel: 'Completed',
				summaryRunning: false,
				issue: null,
			};
		}
		return {
			stages,
			summaryLabel: 'Completed',
			summaryRunning: false,
			issue: {
				kind: 'completed_with_issue',
				text: 'This topic is available, but some content could not be completed.',
			},
		};
	}

	const effectiveStatus = jobStatus ?? progress.status;
	const summaryLabel = effectiveStatus === 'queued' ? 'Queued' : 'Processing';
	return {
		stages,
		summaryLabel,
		summaryRunning: summaryLabel === 'Processing',
		issue: null,
	};
}
